import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ListChecks, FilePlus2, PlayCircle, Loader, ClipboardCheck, CheckCircle2,
  AlertTriangle, Flame, CalendarClock, CalendarRange,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  AreaChart, Area, CartesianGrid, Legend,
} from "recharts";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_COLORS = { New: "#3b82f6", Started: "#6366f1", "In Process": "#0ea5e9", "Under Review": "#f59e0b", Approved: "#10b981", Cancelled: "#94a3b8" };
const PRIO_COLORS = { Critical: "#e11d48", High: "#f97316", Medium: "#fbbf24", Low: "#cbd5e1" };

function Kpi({ icon: Icon, label, value, accent, to, testid }) {
  const inner = (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-md">
      <div className={`mb-3 h-1 w-10 rounded-full ${accent}`} />
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</span>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-1.5 font-heading text-3xl font-extrabold tracking-tight text-slate-900">{value}</div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="flex h-64 items-center justify-center text-slate-400">Loading dashboard...</div>;
  }

  const k = data.kpis;

  return (
    <div className="animate-fade-in space-y-6" data-testid="dashboard-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          {user.role === "owner" ? "Organization Overview" : user.role === "manager" ? "Team Dashboard" : "My Dashboard"}
        </div>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          Welcome back, {user.name.split(" ")[0]}
        </h1>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
        <Kpi icon={ListChecks} label="Total Tasks" value={k.total} accent="bg-primary" to="/tasks" testid="kpi-total-tasks" />
        <Kpi icon={FilePlus2} label="New" value={k.new} accent="bg-blue-500" to="/tasks?status=new" testid="kpi-new" />
        <Kpi icon={Loader} label="In Process" value={(k.started || 0) + (k.in_process || 0)} accent="bg-sky-500" to="/tasks?status=in_process" testid="kpi-in-process" />
        <Kpi icon={ClipboardCheck} label="Pending Review" value={k.under_review} accent="bg-amber-500" to="/tasks?status=under_review" testid="kpi-pending-review" />
        <Kpi icon={CheckCircle2} label="Completed" value={k.approved} accent="bg-emerald-500" to="/tasks?status=approved" testid="kpi-completed" />
        <Kpi icon={AlertTriangle} label="Overdue" value={k.overdue} accent="bg-rose-600" to="/tasks?overdue=true" testid="kpi-overdue" />
        <Kpi icon={Flame} label="High Priority" value={k.high_priority} accent="bg-orange-500" to="/tasks?priority=high" testid="kpi-high-priority" />
        <Kpi icon={CalendarClock} label="Due Today" value={k.due_today} accent="bg-indigo-500" testid="kpi-due-today" />
        <Kpi icon={CalendarRange} label="Due This Week" value={k.due_week} accent="bg-teal-500" testid="kpi-due-week" />
        <Kpi icon={PlayCircle} label="Started" value={k.started} accent="bg-indigo-400" to="/tasks?status=started" testid="kpi-started" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="chart-tasks-by-status">
          <h3 className="font-heading text-lg font-bold text-slate-900">Tasks by Status</h3>
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={data.by_status} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                {data.by_status.map((s) => <Cell key={s.key} fill={STATUS_COLORS[s.name] || "#94a3b8"} />)}
              </Pie>
              <Tooltip />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="chart-tasks-by-priority">
          <h3 className="font-heading text-lg font-bold text-slate-900">Tasks by Priority</h3>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={data.by_priority}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
              <Tooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {data.by_priority.map((p) => <Cell key={p.name} fill={PRIO_COLORS[p.name]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="chart-tasks-by-department">
          <h3 className="font-heading text-lg font-bold text-slate-900">Tasks by Department</h3>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={data.by_department} layout="vertical">
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={95} />
              <Tooltip />
              <Bar dataKey="value" fill="#0D9488" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="chart-created-vs-completed">
        <h3 className="font-heading text-lg font-bold text-slate-900">Created vs Completed - Last 14 Days</h3>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={data.trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
            <Tooltip />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="created" stroke="#1E3A8A" fill="#1E3A8A" fillOpacity={0.12} name="Created" />
            <Area type="monotone" dataKey="completed" stroke="#0D9488" fill="#0D9488" fillOpacity={0.15} name="Completed" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {data.team && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="team-performance-table">
          <h3 className="mb-4 font-heading text-lg font-bold text-slate-900">Team Performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100/80 text-left text-xs uppercase tracking-wider text-slate-600">
                  <th className="px-3 py-2.5 font-semibold">Employee</th>
                  <th className="px-3 py-2.5 font-semibold">Assigned</th>
                  <th className="px-3 py-2.5 font-semibold">Completed</th>
                  <th className="px-3 py-2.5 font-semibold">Pending</th>
                  <th className="px-3 py-2.5 font-semibold">Overdue</th>
                  <th className="px-3 py-2.5 font-semibold">Pending Review</th>
                  <th className="px-3 py-2.5 font-semibold">Completion</th>
                </tr>
              </thead>
              <tbody>
                {data.team.map((m) => (
                  <tr key={m.id} data-testid={`team-row-${m.id}`} className="border-b border-slate-100 transition-colors hover:bg-slate-50/80">
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-slate-800">{m.name}</div>
                      <div className="text-xs text-slate-500">{m.designation}</div>
                    </td>
                    <td className="px-3 py-2.5 font-data">{m.assigned}</td>
                    <td className="px-3 py-2.5 font-data text-emerald-700">{m.completed}</td>
                    <td className="px-3 py-2.5 font-data">{m.pending}</td>
                    <td className="px-3 py-2.5 font-data text-rose-600">{m.overdue}</td>
                    <td className="px-3 py-2.5 font-data text-amber-600">{m.pending_review}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-200">
                          <div className="h-full rounded-full bg-secondary" style={{ width: `${m.completion}%` }} />
                        </div>
                        <span className="font-data text-xs">{m.completion}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.org && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="org-overview-table">
          <h3 className="mb-4 font-heading text-lg font-bold text-slate-900">Department Overview</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100/80 text-left text-xs uppercase tracking-wider text-slate-600">
                  <th className="px-3 py-2.5 font-semibold">Department</th>
                  <th className="px-3 py-2.5 font-semibold">Total</th>
                  <th className="px-3 py-2.5 font-semibold">Completed</th>
                  <th className="px-3 py-2.5 font-semibold">Pending</th>
                  <th className="px-3 py-2.5 font-semibold">Overdue</th>
                  <th className="px-3 py-2.5 font-semibold">Completion</th>
                </tr>
              </thead>
              <tbody>
                {data.org.map((d) => (
                  <tr key={d.department} className="border-b border-slate-100 transition-colors hover:bg-slate-50/80">
                    <td className="px-3 py-2.5 font-semibold text-slate-800">{d.department}</td>
                    <td className="px-3 py-2.5 font-data">{d.total}</td>
                    <td className="px-3 py-2.5 font-data text-emerald-700">{d.completed}</td>
                    <td className="px-3 py-2.5 font-data">{d.pending}</td>
                    <td className="px-3 py-2.5 font-data text-rose-600">{d.overdue}</td>
                    <td className="px-3 py-2.5 font-data">{d.completion}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
